import { CONTACT, LEGAL_ENTITY, SITE } from '@/constants/site.constants';
import type { LegalDocumentContent } from '@/types/legal.types';

/**
 * The page behind the App Store / Play "Support URL". Store review opens it and looks
 * for one thing: how a user gets help and reaches a human. A terms or privacy page in
 * that field is a routine metadata rejection, which is why this exists separately.
 *
 * Every answer here must describe what the app actually does today. An FAQ that
 * promises a flow we have not shipped is worse than no FAQ.
 */
export const SUPPORT_CONTENT: LegalDocumentContent = {
  title: `${SITE.name} Support`,
  summary: `Need help with ${SITE.name}? Email ${CONTACT.email} and a person will get back to you. The answers below cover the questions we are asked most often.`,
  sections: [
    {
      id: 'contact',
      heading: '1. Contact us',
      paragraphs: [
        `The fastest way to reach us is email: ${CONTACT.email}. We read every message and reply from a real address, not a no-reply.`,
        'Please include the email address on your account, and — if your question is about a rental — the item name and the dates involved. That lets us look up the booking without a round trip.',
      ],
    },
    {
      id: 'account',
      heading: '2. Account and sign-in',
      bullets: [
        `${SITE.name} is open to verified college students. You sign in with your college email address and a one-time code we send to it — there is no password to remember or lose.`,
        'If the code does not arrive, check your spam folder first, then confirm the address is the college one you registered with.',
        `If your campus is not listed yet, email ${CONTACT.email} with your college name and we will tell you where it sits in our rollout.`,
        'You can delete your account and its data at any time from inside the app, under Profile. The Delete Account page on this site explains exactly what is removed and what we are required to retain.',
      ],
    },
    {
      id: 'rentals',
      heading: '3. Rentals, deposits and payments',
      bullets: [
        'Bookings run between two students. Shoutt verifies the payment, holds the security deposit for the duration of the rental, and releases it after the item is returned and the return is confirmed.',
        'If a payment does not show against your booking, email us with the booking reference and the transaction details and we will trace it.',
        'If an item comes back damaged, or does not come back, raise it from the booking in the app. Disputes are reviewed by a member of our team, not resolved automatically.',
        `For anything about a refund or a deposit that has not been released, email ${CONTACT.email} with the booking reference.`,
      ],
    },
    {
      id: 'safety',
      heading: '4. Reporting a user, listing or message',
      paragraphs: [
        `Every listing, Shoutt and profile in the app has a report option. Reports go to a moderation queue and are reviewed by a person at ${SITE.name}.`,
        `You can also report to us directly at ${CONTACT.email}. If a report concerns the safety of a minor, see our Child Safety Standards page — those reports are prioritised above all others.`,
        'If you believe you are in immediate danger, contact your local police first.',
      ],
    },
    {
      id: 'grievance',
      heading: '5. Grievance redressal',
      paragraphs: [
        'If a support request has not been resolved to your satisfaction, you can escalate it to our Grievance Officer, appointed under applicable Indian law.',
      ],
      bullets: [
        `Grievance Officer: ${LEGAL_ENTITY.grievanceOfficer}`,
        `Designation: ${LEGAL_ENTITY.grievanceOfficerDesignation}`,
        `Email: ${CONTACT.grievanceEmail}`,
      ],
    },
    {
      id: 'company',
      heading: '6. Who operates Shoutt',
      bullets: [
        `${SITE.name} is operated by ${LEGAL_ENTITY.name}.`,
        `Registered address: ${LEGAL_ENTITY.address}.`,
        'Our Terms of Use, Rental Terms, Privacy Policy and Child Safety Standards are published on this site.',
      ],
    },
  ],
};
